import axios from 'axios'
import { LOOKUP_FETCH_LIMIT, type AuthorSalutation } from '../shared/constants'
import { CLIENT_API_TEXTS as TX } from '../shared/texts'

// All staff-UI calls go through here. The server serves the UI, so a relative
// /api base works in dev (vite-express) and prod alike. withCredentials sends the
// session cookie on every request.
export const api = axios.create({ baseURL: '/api', withCredentials: true })

// ---- Types (mirror the server responses we use) ----

export interface Health {
    ok: boolean
    service: string
    version?: string
    // Server run mode. Anything other than 'production' paints the dev stripe (App.tsx).
    mode?: string
    // Whether the dev-safe catch is active (schedulers off, live keys refused).
    devSafe?: boolean
    // Stamp of the nightly image. Empty strings on every other instance — a local dev
    // server has no BUILD_DATE, so the band stays textless there (App.tsx).
    build?: { date: string, commit: string }
    // Test-mail mode: 'on' = every mail to the test address, 'missing-address' = none is sent.
    mailRedirect?: 'off' | 'on' | 'missing-address' | 'unknown'
    db: 'connected' | 'disconnected'
    time: string
}

export interface Stats {
    invoicesTotal: number
    invoicesByStatus: Record<string, number>
    unsent: number
    subscriptionsTotal: number
}

export interface Invoice {
    _id: string
    brand: string
    status: 'draft' | 'finalized' | 'sent' | 'void'
    invoiceNumber?: string
    referenceNumber?: number
    billing: { displayName: string, company?: string, email: string, country?: string }
    currency: string
    totals: { net: number, tax: number, gross: number }
    periodLabel?: string
    // Set on a CREDIT NOTE → the invoice it reverses. Present = this document is itself a
    // reversal and can never be refunded (the server refuses it, the UI hides the button).
    creditNoteFor?: string
    customerId?: string
    // Gross reversed by credit notes (> 0 = cancelled/refunded) — the list leaves those out of a
    // collective dispatch.
    credited?: number
    // The PDF's outgoing name (Einstellungen, e.g. „D1317-26103102.pdf") — the viewer tab's title. None for a draft.
    pdfName?: string
    createdAt: string
    updatedAt?: string // a draft's save sends it back as expectedUpdatedAt — 409 if it moved
}

export interface CountrySummary {
    country: string
    net: number
    tax: number
    gross: number
    count: number
}

// One OSS return line: destination country at one tax rate (EU One-Stop-Shop scheme).
export interface OssSummary {
    country: string
    taxRate: number
    net: number
    tax: number
    count: number
}

export interface MonthlyReport {
    /** Dateiname des Berichts-PDFs — Ende seiner Adresse. */
    pdfName: string
    year: number
    month: number
    currency: string
    rows: unknown[]
    perCountry: CountrySummary[]
    totals: { net: number, tax: number, gross: number }
    // EU cross-border B2C only — a SUBSET of perCountry, never to be added to it.
    oss: OssSummary[]
    ossTotals: { net: number, tax: number }
    ossBaseCountry: string
}

export interface Customer {
    _id: string
    displayName: string
    company?: string
    email: string
    address?: { line1?: string, line2?: string, postalCode?: string, city?: string, country?: string }
    vatId?: string
    isBusiness: boolean
    notes?: string
    invoiceMailSubject?: string
    invoiceMailBody?: string
    customerNumber?: number
    createdAt: string
    updatedAt?: string // sent back on save as expectedUpdatedAt — refused (409) if it moved
}

export interface LineItem {
    description: string
    quantity: number
    unitPrice: number
    taxRate: number
}

export type RunTiming = 'last-day' | 'next-month-first' | 'day-of-month'

export interface Subscription {
    _id: string
    brand: string
    customerId: string
    cadence: 'monthly' | 'yearly'
    mode: 'auto' | 'draft'
    status: 'active' | 'paused' | 'ended'
    source?: 'manual' | 'stripe' | 'mollie'
    runTiming: RunTiming
    runDayOfMonth: number
    items: LineItem[]
    currency: string
    nextRunAt?: string
    // Manual abos (month-end run, since 30.09.2026): calendar days as ISO strings.
    startDate?: string
    endDate?: string
    nextBillingDate?: string
    billingDay?: number
    lastRunError?: string
    lastRunErrorAt?: string
    statusSince?: string // manual abos: the day it was paused / ended (calendar day)
    // Paid-until, mirrored from the PSP (Mollie's nextPaymentDate / Stripe's period end). For a
    // PSP-driven subscription this is the only date we actually know — nextRunAt is ours and
    // never runs for those.
    currentPeriodEnd?: string
    createdAt: string
    updatedAt?: string // sent back on save as expectedUpdatedAt — refused (409) if it moved
    customerName?: string // resolved server-side by the paginated list endpoint
    product?: string // first position's text (list endpoint) — tells a customer's abos apart
    netPrice?: number | null // list endpoint: Σ quantity × unit price; null = no position („—“, not 0)
}

export interface Settings {
    // Eigene Bankverbindung fuer den SEPA-Sammelauftrag. Die IBAN kommt nie im Klartext
    // hierher — nur „gesetzt" und die letzten Stellen, wie bei jedem Geheimnis.
    sepaCreditorName: string
    /** Im Klartext — anders als die uebrigen Geheimnisfelder. Der Betreiber tippt sie ein und
     *  muss sie pruefen koennen; gespeichert bleibt sie verschluesselt. */
    sepaIban: string
    sepaBic: string
    stripeTestmode: boolean
    stripeTestKeySet: boolean
    stripeTestKeyHint: string
    stripeLiveKeySet: boolean
    stripeLiveKeyHint: string
    stripeTestWebhookSecretSet: boolean
    stripeTestWebhookSecretHint: string
    stripeLiveWebhookSecretSet: boolean
    stripeLiveWebhookSecretHint: string
    stripeTestWebhookEndpointSet: boolean
    stripeLiveWebhookEndpointSet: boolean
    converterUrl: string
    converterWebhookSecretSet: boolean
    converterWebhookSecretHint: string
    checkoutApiKeySet: boolean
    checkoutApiKeyHint: string
    paypalTestmode: boolean
    paypalTestClientId: string
    paypalTestSecretSet: boolean
    paypalTestSecretHint: string
    paypalLiveClientId: string
    paypalLiveSecretSet: boolean
    paypalLiveSecretHint: string
    mollieBalanceTokenSet: boolean
    mollieBalanceTokenHint: string
    taxAdvisorEmail: string
    taxAdvisorBcc: string
    smtpHost: string
    smtpPort: number
    smtpSecure: boolean
    smtpUser: string
    smtpPassSet: boolean
    smtpFrom: string
    testMailTo?: string
    testMailMode?: boolean
    // The backoffice currency (ISO 4217) and whether it is fixed (there are documents).
    currency: string
    currencyLocked: boolean
    // Dev-safe instance: the mode is on regardless of the switch.
    testMailForced?: boolean
    imapHost: string
    imapPort: number
    imapSecure: boolean
    imapUser: string
    imapPassSet: boolean
    imapMailbox: string
    imapTrashMailbox: string
    ingestNotifyEmail: string
    adminNotifyEmail: string
    ingestEnabled: boolean
    publicBaseUrl: string
    invoiceMailSubject: string
    invoiceMailBody: string
    singleInvoiceMailSubject: string
    singleInvoiceMailBody: string
    creditNoteMailSubject: string
    exportNoMovementLine: string
    pspBalanceUnavailableNote: string
    exportReportFilePrefix: string
    invoicePdfNamePattern: string
    creditNoteMailBody: string
    llmEnabled: boolean
    llmUrl: string
    llmModel: string
    llmTimeoutMs: number
    aboRunHour: number
    ingestPollHours: number
    paymentAutoImportEnabled: boolean
    paymentAutoImportDay: number
    paymentAutoImportTime: string
    paymentAutoImportLastPeriod: string
    logLevelDefault: string
    logLevelOverrides: Record<string, string>
    logRetentionDays: number
    vatOssMode: 'off' | 'manual' | 'auto'
    vatBaseCountry: string
    vatRateOverrides: Record<string, number>
    vatOssThreshold: number
    // Zertifikat-Selbstversand (Absender, Etikett/Umschlag, Benachrichtigung).
    certSenderName: string
    certSenderLine1: string
    certSenderLine2: string
    certSenderPostalCode: string
    certSenderCity: string
    certSenderCountry: string
    certAddressOutput: 'label' | 'endless' | 'envelope'
    certLabelPreset: string
    certLabelFillDirection: 'row' | 'column'
    certLabelWidthMm: number
    certLabelHeightMm: number
    certLabelColumns: number
    certLabelRows: number
    certLabelMarginTopMm: number
    certLabelMarginLeftMm: number
    certLabelGapXMm: number
    certLabelGapYMm: number
    certLabelStartPosition: number
    certEnvelopeFormat: 'C4' | 'C5' | 'DL'
    certErrorNotifyTo: string
    certNotifyMailEnabled: boolean
    certNotifyMailTo: string
    certNotifyTelegramEnabled: boolean
    certTelegramBotTokenSet: boolean
    certTelegramBotTokenHint: string
    certTelegramChatIds: string
}

export interface VatStatus {
    ossMode: 'off' | 'manual' | 'auto'
    ossEnabled: boolean
    baseCountry: string
    rateOverrides: Record<string, number>
    threshold: number
    revenueYtd: number
    year: number
    defaults: Record<string, number>
}

// ---- Calls ----

export const getHealth = () => api.get<Health>('/health').then((r) => r.data)
// Where the test-mail mode sends (behind the login — /api/health never names the address).
export const getMailMode = () => api.get<{ active: boolean, to: string }>('/mail-mode').then((r) => r.data)
export const getStats = () => api.get<Stats>('/stats').then((r) => r.data)

export const listInvoices = (params: ListParams = {}) =>
    api.get<ListResult<Invoice>>('/invoices', { params }).then((r) => r.data)
export const finalizeInvoice = (id: string) => api.post(`/invoices/${id}/finalize`).then((r) => r.data)
// The reverse-charge decision for a customer as finalize will take it (services/vat.ts).
export interface TaxDecision { reverseCharge: boolean, code: string, taxPercent: number, vatId: string, country: string }
export const getTaxDecision = (customerId: string) =>
    api.get<TaxDecision>('/invoices/tax-decision', { params: { customerId } }).then((r) => r.data)
// With a name the address ENDS on it — Safari's PDF viewer saves under the iframe's own src, the
// redirect target never reaches it (operator, 01.11.2026, v0.17.35). Without one: the old address, which redirects.
// ONE tab per invoice, whichever page opens it — list, form or month export (operator, 01.11.2026).
export const invoicePdfTabId = (id: string) => `invoice-pdf-${id}`
export const invoicePdfUrl = (id: string, name?: string) =>
    name ? `/api/invoices/${id}/pdf/${encodeURIComponent(name)}` : `/api/invoices/${id}/pdf`

// Refund: preview first (amount, fee, payout — or why it is impossible), then trigger. The
// credit note and the access revoke are booked by the PSP webhook, so they arrive seconds
// later, not in this response.
export interface RefundPreview {
    invoiceId: string
    invoiceNumber?: string
    brand: string
    gross: number
    alreadyRefunded: number
    refundable: number
    fee: { fee: number, type: FeeType, value: number, source: 'none' | 'brand' | 'item', itemId?: number, label?: string }
    payout: number
    itemIds: number[]
    paymentId?: string
    provider?: string
    blocker?: string
    // Possible, but not as it should be — e.g. the cancellation fee of the brand is not set.
    warning?: string
}
// Send or re-send one document (status-independent: a lost mail, a corrected address).
// Several hand-picked documents of ONE customer (server checks: one customer, no drafts, none
// cancelled). Split into one mail per brand and kind.
// The send drawer: the mails as they would go out now, filled from the templates by the SERVER.
export interface MailPreview {
    kind: 'invoice' | 'creditNote'
    brand: string
    to: string
    testMailTo: string
    testMailBlocked: boolean
    from: string
    fromBlocked?: string
    invoiceNumbers: string[]
    alreadySent: string[]
    subject: string
    html: string
}
export interface MailOverride { subject?: string, html?: string }
export type MailOverrides = Partial<Record<'invoice' | 'creditNote', MailOverride>>
export const getSendPreview = (id: string) => api.get<MailPreview[]>(`/invoices/${id}/send-preview`).then((r) => r.data)
export const getSendPreviewSelected = (ids: string[]) => api.post<MailPreview[]>('/invoices/send-preview', { ids }).then((r) => r.data)
export const sendSelectedInvoices = (ids: string[], overrides?: MailOverrides) =>
    api.post<{ sent: boolean, count: number, to?: string, invoiceNumbers: string[], redirectedTo?: string, blocked?: string, mails: { sent: boolean, invoiceNumbers: string[] }[] }>(
        '/invoices/send-selected', { ids, overrides }).then((r) => r.data)
export const sendInvoice = (id: string, override?: MailOverride) =>
    api.post<{ sent: boolean, count: number, to?: string, invoiceNumbers: string[], redirectedTo?: string }>(`/invoices/${id}/send`, override ?? {}).then((r) => r.data)

export const getRefundPreview = (id: string) =>
    api.get<RefundPreview>(`/invoices/${id}/refund`).then((r) => r.data)
export type RefundReason = 'cancellation' | 'withdrawal'
// Sources without a PSP (server: NO_PSP_SOURCES) — cancelled, never refunded.
export const NO_PSP_SOURCES = ['manual', 'subscription']
export const cancelInvoice = (id: string) =>
    api.post<{ creditNoteId: string, invoiceNumber: string }>(`/invoices/${id}/cancel`).then((r) => r.data)
export const reissueInvoice = (id: string) =>
    api.post<Invoice>(`/invoices/${id}/reissue`).then((r) => r.data)
export const duplicateInvoice = (id: string) =>
    api.post<Invoice>(`/invoices/${id}/duplicate`).then((r) => r.data)
export const refundInvoice = (id: string, body: { mode?: 'full' | 'partial', amount?: number, fee?: number, reason?: RefundReason }) =>
    api.post<{ ok: true, paymentId: string, amount: number, fee: number, cancelAccess: boolean }>(`/invoices/${id}/refund`, body).then((r) => r.data)

// Full invoice (incl. positions) for the editor/detail view.
export interface InvoiceDetail extends Invoice {
    customerId: string
    country?: string
    lineItems: LineItem[]
    // The PSP side: paid? refunded? which payment? null for invoices predating the mirrored
    // transactions (v0.9.11) and for manually created ones that never had a payment.
    // Refund state, so an open tab can lock the button instead of finding out at the server.
    refundedAmount?: number
    // Chargeback (Rückbelastung): only a mark — no document went to the customer.
    chargedBackAt?: string
    chargedBackAmount?: number
    chargebackReversedAt?: string
    refundIntent?: { amount: number, fee: number, requestedAt: string, reason?: RefundReason }
    // How the sale came about; manual + abo-run invoices have no PSP → cancel, not refund.
    source?: string
    // Gross reversed by credit notes so far (> 0 = cancelled/refunded).
    credited?: number
    // Imported from an earlier system (reprint) — its text is never changed.
    legacy?: { system: string }
    // Text changes of the issued invoice (admin, 03.11.2026) — shown only in the invoice view.
    textChanges?: InvoiceTextChange[]
    payment?: {
        provider: string
        providerPaymentId: string
        status: TxStatus
        amount: number
        amountRefunded: number
        currency: string
        occurredAt?: string
    } | null
}
export interface InvoiceTextChange {
    _id: string
    lineIndex: number
    from: string
    to: string
    reason: string
    wasSent: boolean
    by: string
    at: string
    pdfShaBefore: string
    pdfShaAfter: string
}
export const changeInvoiceLineText = (id: string, body: { lineIndex: number, text: string, reason: string }) =>
    api.put<InvoiceTextChange>(`/invoices/${id}/line-text`, body).then((r) => r.data)
export const textChangePdfUrl = (id: string, changeId: string) => `/api/invoices/${id}/text-changes/${changeId}/pdf`
export const getInvoice = (id: string) => api.get<InvoiceDetail>(`/invoices/${id}`).then((r) => r.data)
export const updateInvoice = (id: string, data: CreateInvoiceInput & { expectedUpdatedAt?: string }) =>
    api.put<InvoiceDetail>(`/invoices/${id}`, data).then((r) => r.data)
export const deleteInvoice = (id: string) => api.delete(`/invoices/${id}`).then((r) => r.data)

export const getReport = (y: number, m: number) =>
    api.get<MonthlyReport>(`/reports/${y}/${m}`).then((r) => r.data)
export const reportPdfUrl = (y: number, m: number, name?: string) =>
    name ? `/api/reports/${y}/${m}/pdf/${encodeURIComponent(name)}` : `/api/reports/${y}/${m}/pdf`

export const importStripe = (y: number, m: number) =>
    api.post<{ count: number, paths: string[], skipped: string[] }>(`/stripe/import/${y}/${m}`).then((r) => r.data)

export const importPaypal = (y: number, m: number) =>
    api.post<{ count: number, path: string | null }>(`/paypal/import/${y}/${m}`).then((r) => r.data)

export const importStripeReports = (y: number, m: number) =>
    api.post<{ files: string[], skipped: string[] }>(`/stripe/reports/${y}/${m}`).then((r) => r.data)
// Mollie account statement per brand — same shape as the Stripe/PayPal imports.
export const importMollie = (y: number, m: number) =>
    api.post<{ count: number, path: string | null }>(`/mollie/import/${y}/${m}`).then((r) => r.data)

export const sendZip = (y: number, m: number, to?: string) =>
    api.post(`/exports/${y}/${m}/send`, to ? { to } : {}).then((r) => r.data)

// ---- Monthly accounting directory (file explorer) ----

// displayName: the shown name (an invoice PDF's outgoing name); name stays the stored file and the key.
export interface DocFileEntry { name: string, displayName?: string, invoiceId?: string, size: number, modified: string, amount?: number, docId?: string, expenseType?: string }
export interface DocFolderListing { name: string, files: DocFileEntry[] }
export interface MonthListing {
    year: number
    month: number
    exists: boolean
    folders: DocFolderListing[]
    rootFiles: DocFileEntry[]
}

// Existing years → months on disk (drives the Jahr/Monat columns).
export const getDocumentsIndex = () =>
    api.get<Record<string, number[]>>('/documents/index').then((r) => r.data)

export const listMonthDocuments = (y: number, m: number) =>
    api.get<MonthListing>(`/documents/${y}/${m}`).then((r) => r.data)
export const ensureMonthDocuments = (y: number, m: number) =>
    api.post<MonthListing>(`/documents/${y}/${m}`).then((r) => r.data)
export const uploadDocuments = (y: number, m: number, folder: string, files: File[]) => {
    const fd = new FormData()
    for (const f of files) fd.append('files', f)
    return api.post<{ uploaded: string[], listing: MonthListing }>(`/documents/${y}/${m}/upload`, fd, { params: { folder } }).then((r) => r.data)
}
export const deleteDocument = (y: number, m: number, folder: string, name: string) =>
    api.delete<MonthListing>(`/documents/${y}/${m}/file`, { params: { folder, name } }).then((r) => r.data)
// Move a file to another month/year and/or category. Returns the SOURCE listing.
export const moveDocument = (y: number, m: number, folder: string, name: string, toYear: number, toMonth: number, toFolder?: string) =>
    api.post<MonthListing>(`/documents/${y}/${m}/file/move`, { toYear, toMonth, toFolder }, { params: { folder, name } }).then((r) => r.data)
// Set/clear the stored amount of a file's Document (null clears). Returns the listing.
export const setDocumentAmount = (y: number, m: number, folder: string, name: string, amount: number | null) =>
    api.patch<MonthListing>(`/documents/${y}/${m}/file/amount`, { amount }, { params: { folder, name } }).then((r) => r.data)
// Set/clear the expense type of a vendor invoice (null clears). Returns the listing.
export const setDocumentExpenseType = (y: number, m: number, folder: string, name: string, expenseType: string | null) =>
    api.patch<MonthListing>(`/documents/${y}/${m}/file/expense-type`, { expenseType }, { params: { folder, name } }).then((r) => r.data)
// `shown` ends the path (see invoicePdfUrl); `name` is the stored file, the key the server goes by.
export const documentFileUrl = (y: number, m: number, folder: string, name: string, shown = name) =>
    `/api/documents/${y}/${m}/file/${encodeURIComponent(shown)}?folder=${encodeURIComponent(folder)}&name=${encodeURIComponent(name)}`
// Delete an empty month / empty year folder. Both return the refreshed year→months index.
export const deleteMonthDirectory = (y: number, m: number) =>
    api.delete<Record<string, number[]>>(`/documents/${y}/${m}`).then((r) => r.data)
export const deleteYearDirectory = (y: number) =>
    api.delete<Record<string, number[]>>(`/documents/${y}`).then((r) => r.data)

// ---- Monthly checklist (gated send) ----

export interface ChecklistItem {
    id: string
    label: string
    required: boolean
    manualDone: boolean
    manualDoneAt?: string
    binding?: string
    autoSatisfied: boolean
    done: boolean
    // „muss da sein“ | „optional“ | „kann leer sein“ — set at the item, valid for every month.
    requirement: ChecklistRequirement
    mailNote: boolean
    canBeEmpty: boolean
    // Done because the import noted "no movement" for this provider and month.
    noMovement?: { brands: string[] }
}
export type ChecklistRequirement = 'required' | 'optional' | 'may-be-empty'
export interface ChecklistView {
    year: number
    month: number
    items: ChecklistItem[]
    allDone: boolean
    doneCount: number
    requiredCount: number
    // The month was sent: the list is the snapshot of that moment and cannot be edited.
    frozen: boolean
    // Set once the month's ZIP went to the tax advisor — the month is handed over.
    // Belege that arrive later are filed into the next open month.
    closedAt?: string
    // When the ZIP actually left and to whom — a fact, as opposed to closedAt, which
    // can also be set by hand.
    sentAt?: string
    sentTo?: string
}
export interface ChecklistBinding { key: string, label: string, canBeEmpty: boolean }

export const getChecklist = (y: number, m: number) =>
    api.get<ChecklistView>(`/checklist/${y}/${m}`).then((r) => r.data)
export const getChecklistBindings = () =>
    api.get<ChecklistBinding[]>('/checklist/bindings').then((r) => r.data)
export const addChecklistItem = (y: number, m: number, data: { label: string, binding?: string }) =>
    api.post<ChecklistView>(`/checklist/${y}/${m}/items`, data).then((r) => r.data)
export const setChecklistItem = (y: number, m: number, id: string, done: boolean) =>
    api.patch<ChecklistView>(`/checklist/${y}/${m}/items/${id}`, { done }).then((r) => r.data)
export const updateChecklistItem = (y: number, m: number, id: string, patch: { label?: string, binding?: string, requirement?: ChecklistRequirement, mailNote?: boolean }) =>
    api.put<ChecklistView>(`/checklist/${y}/${m}/items/${id}`, patch).then((r) => r.data)
export const deleteChecklistItem = (y: number, m: number, id: string) =>
    api.delete<ChecklistView>(`/checklist/${y}/${m}/items/${id}`).then((r) => r.data)
export const setMonthClosed = (y: number, m: number, closed: boolean) =>
    api.post<ChecklistView>(`/checklist/${y}/${m}/closed`, { closed }).then((r) => r.data)

// Row shape of the paginated customer list: slim customer fields + per-customer
// metrics aggregated server-side. openCount/openGross = finalized-but-unsent (OP).
export interface CustomerRow {
    _id: string
    displayName: string
    company?: string
    email: string
    address?: { postalCode?: string, city?: string, country?: string }
    vatId?: string
    isBusiness: boolean
    createdAt: string
    invoiceCount: number
    openCount: number
    openGross: number
    subscriptionCount: number
    customerNumber?: number
}

export interface ListParams {
    q?: string
    sort?: string
    dir?: 'asc' | 'desc'
    page?: number
    pageSize?: number
    // List-specific filter params (e.g. status, brand, cadence) are merged in too.
    [key: string]: string | number | undefined
}
// total = count after search/filter; grandTotal = unfiltered count (for "x von y").
export interface ListResult<T> { rows: T[], total: number, grandTotal: number }

// ── Transaktionen (PSP-Zahlungen) ────────────────────────────────────────────
export type TxStatus = 'open' | 'paid' | 'failed' | 'expired' | 'refunded' | 'partially_refunded'
export type TxKind = 'first' | 'recurring' | 'oneoff' | 'unknown'

export interface Transaction {
    _id: string
    brand: string
    provider: string
    providerPaymentId: string
    status: TxStatus
    kind: TxKind
    amount: number
    amountRefunded: number
    currency: string
    externalId?: string
    customerEmail?: string
    customerId?: string
    subscriptionId?: string
    invoiceId?: string
    occurredAt?: string
    receivedAt: string
}

export interface TransactionHistoryEntry {
    at: string
    type: 'status' | 'refund'
    status?: string
    reason?: string
    refundId?: string
    amount?: number
}

export interface TransactionDetail extends Omit<Transaction, 'customerId' | 'subscriptionId' | 'invoiceId'> {
    history: TransactionHistoryEntry[]
    metadata?: Record<string, string>
    providerCustomerId?: string
    providerSubscriptionId?: string
    // Populated on the detail route.
    customerId?: { _id: string, displayName?: string, company?: string, email?: string }
    subscriptionId?: { _id: string, externalId?: string, status?: string, providerSubscriptionId?: string }
    invoiceId?: { _id: string, invoiceNumber?: string, status?: string, currency?: string, totals?: { gross: number } }
}

export interface TransactionListResult extends ListResult<Transaction> {
    sums: { amount: number, refunded: number }
}

export const listTransactions = (params: ListParams = {}) =>
    api.get<TransactionListResult>('/transactions', { params }).then((r) => r.data)
export const getTransaction = (id: string) =>
    api.get<TransactionDetail>(`/transactions/${id}`).then((r) => r.data)

export const listCustomers = (params: ListParams = {}) =>
    api.get<ListResult<CustomerRow>>('/customers', { params }).then((r) => r.data)
// For the customer picker (invoice/subscription forms): a server-side search, so every
// customer is reachable — the former fixed list stopped at 200 and hid everyone after.
// `q` matches company, name, e-mail, city, VAT ID and (digits) the customer number.
export const searchCustomerOptions = (q: string, pageSize: number) =>
    listCustomers({ q, pageSize, sort: 'company', dir: 'asc' }).then((r) => r.rows)
// Whether any customer exists at all — what the forms' "create one first" notice asks.
export const hasCustomers = () =>
    listCustomers({ pageSize: 1 }).then((r) => r.grandTotal > 0)
export const getCustomer = (id: string) => api.get<Customer>(`/customers/${id}`).then((r) => r.data)
export const createCustomer = (data: Partial<Customer>) =>
    api.post<Customer>('/customers', data).then((r) => r.data)
export const updateCustomer = (id: string, data: Partial<Customer> & { expectedUpdatedAt?: string }) =>
    api.put<Customer>(`/customers/${id}`, data).then((r) => r.data)
// Soft-delete (move to trash). Restore/purge are admin-only on the server.
export const deleteCustomer = (id: string) =>
    api.delete(`/customers/${id}`).then((r) => r.data)
export const restoreCustomer = (id: string) =>
    api.post(`/customers/${id}/restore`).then((r) => r.data)
export const purgeCustomer = (id: string) =>
    api.delete(`/customers/${id}/purge`).then((r) => r.data)

export interface CreateInvoiceInput {
    brand: string
    customerId: string
    lineItems: LineItem[]
    periodLabel?: string // no currency: issued in the backoffice currency
}
export const createInvoice = (data: CreateInvoiceInput) =>
    api.post<Invoice>('/invoices', data).then((r) => r.data)

export const listSubscriptions = (params: ListParams = {}) =>
    api.get<ListResult<Subscription>>('/subscriptions', { params }).then((r) => r.data)
export const getSubscription = (id: string) => api.get<Subscription>(`/subscriptions/${id}`).then((r) => r.data)
export const updateSubscription = (id: string, data: Partial<CreateSubscriptionInput> & { status?: string, expectedUpdatedAt?: string }) =>
    api.put<Subscription>(`/subscriptions/${id}`, data).then((r) => r.data)
export interface CreateSubscriptionInput {
    brand: string
    customerId: string
    cadence: 'monthly' | 'yearly'
    mode?: 'auto' | 'draft'
    runTiming?: RunTiming
    runDayOfMonth?: number
    items: LineItem[] // no currency: billed in the backoffice currency
    // Manual abos: 'YYYY-MM-DD'. No end date — a manual abo is ended through its status.
    startDate?: string
    nextBillingDate?: string
}
export const createSubscription = (data: CreateSubscriptionInput) =>
    api.post<Subscription>('/subscriptions', data).then((r) => r.data)
export interface AboRunResult {
    cutoff: string
    count: number
    invoices: { invoiceId: string, customerId: string, brand: string, subscriptionIds: string[], positions: number, status: string }[]
    errors: { subscriptionId: string, error: string }[]
}
export const runSubscriptions = () =>
    api.post<AboRunResult>('/subscriptions/run').then((r) => r.data)
// Soft-delete (move to trash). Restore/purge are admin-only on the server.
export const deleteSubscription = (id: string) =>
    api.delete(`/subscriptions/${id}`).then((r) => r.data)
export const restoreSubscription = (id: string) =>
    api.post(`/subscriptions/${id}/restore`).then((r) => r.data)
export const purgeSubscription = (id: string) =>
    api.delete(`/subscriptions/${id}/purge`).then((r) => r.data)

// ---- Mehrfachauswahl → Papierkorb (Kunden, Abos, Benutzer, Autoren) ----
// One endpoint pair per kind, same shape for all: the ids behind the list's current search and
// filters („alle N im Filter“), and moving a selection — with what was NOT moved and why.
export type BulkTrashKind = 'customers' | 'subscriptions' | 'users' | 'authors'
export interface BulkTrashResult { moved: number, skipped: { id: string, label: string, reason: string }[] }
export const listIdsInFilter = (kind: BulkTrashKind, params: Record<string, string | undefined>) =>
    api.get<{ ids: string[], truncated: boolean }>(`/${kind}/ids`, { params }).then((r) => r.data)
export const moveSelectionToTrash = (kind: BulkTrashKind, ids: string[]) =>
    api.post<BulkTrashResult>(`/${kind}/trash`, { ids }).then((r) => r.data)

// ---- Trash (admin only) ----
export interface TrashCustomer {
    _id: string
    label: string
    email: string
    deletedAt: string
    deletedByName: string | null
    relations: { invoices: number, subscriptions: number }
}
export interface TrashSubscription {
    _id: string
    label: string
    brand: string
    cadence: 'monthly' | 'yearly'
    deletedAt: string
    deletedByName: string | null
    relations: { invoices: number }
}
export interface TrashUser {
    _id: string
    label: string
    email: string
    role: string
    deletedAt: string
    deletedByName: string | null
    relations: Record<string, number>
}
export interface TrashAuthor {
    _id: string, label: string, email: string, externalRef: string,
    deletedAt: string, deletedByName: string | null,
    relations: { statements: number, sales: number, items: number },
}
export interface TrashResult {
    customers: TrashCustomer[], subscriptions: TrashSubscription[],
    users: TrashUser[], authors: TrashAuthor[],
}
export const listTrash = () => api.get<TrashResult>('/trash').then((r) => r.data)

export const getSettings = () => api.get<Settings>('/settings').then((r) => r.data)
export const saveSettings = (patch: Record<string, unknown>) =>
    api.put<Settings>('/settings', patch).then((r) => r.data)
export const getVatStatus = () => api.get<VatStatus>('/settings/vat-status').then((r) => r.data)

export interface StripeWebhookSetupResult {
    mode: 'test' | 'live'
    url: string
    endpointId: string
    created: boolean
    secretCaptured: boolean
    message: string
}
// Create/update the Stripe webhook endpoint via the Stripe API (auto-captures the secret).
export const setupStripeWebhook = () =>
    api.post<StripeWebhookSetupResult>('/settings/stripe-webhook').then((r) => r.data)

// ── Per-brand config (multi-brand) ────────────────────────────────────────────

export interface BrandConfig {
    brand: string
    name: string
    isDefaultBrand: boolean
    paymentProvider: 'stripe' | 'mollie'
    mollieTestmode: boolean
    mollieTestKeySet: boolean
    mollieTestKeyHint: string
    mollieLiveKeySet: boolean
    mollieLiveKeyHint: string
    stripeTestmode: boolean
    stripeTestKeySet: boolean
    stripeTestKeyHint: string
    stripeLiveKeySet: boolean
    stripeLiveKeyHint: string
    stripeTestWebhookSecretSet: boolean
    stripeTestWebhookSecretHint: string
    stripeLiveWebhookSecretSet: boolean
    stripeLiveWebhookSecretHint: string
    stripeTestWebhookEndpointSet: boolean
    stripeLiveWebhookEndpointSet: boolean
    converterEnabled: boolean
    converterUrl: string
    converterWebhookSecretSet: boolean
    converterWebhookSecretHint: string
    vitalpilzeWebhookUrl: string
    vitalpilzeWebhookSecretSet: boolean
    vitalpilzeWebhookSecretHint: string
    refundFeeType: FeeType
    refundFeeValue: number | null   // null = nicht gesetzt
    refundFeeOverrides: RefundFeeOverride[]
    // null = same VAT rate as the reversed invoice
    // Author settlement: the brand-wide default fee, overridden per author or per article.
    authorFeeType: FeeType
    authorFeeValue: number | null   // null = nicht gesetzt
    authorPayoutThreshold: number | null   // Mindestauszahlung der Marke; null = nicht gesetzt
    // null = nicht gesetzt (Warnung bei jeder Rückbelastung), 0 = bewusst keine Gebühr
    chargebackFeeFlat: number | null
    // Settles this brand authors at all? Switched under Allgemein → Dozenten-Einstellungen;
    // off hides the whole author panel in the brand mask. Missing on the server = on.
    authorSettlementEnabled: boolean
    // Who issues the author credit notes (Art. 224 MwStSystRL needs the issuer in full).
    issuerName: string
    issuerAddress: string
    issuerTaxNumber: string
    issuerRegister: string
    issuerVatId: string
    // The same address again, in parts — the free-text block above stays as it is, because the
    // author credit note renders from it. These feed the e-invoice (EN 16931), which needs the
    // country code as its own term and will not take a text block.
    issuerStreet: string
    issuerPostalCode: string
    issuerCity: string
    issuerCountry: string
    issuerEmail: string
    // Link im Fuß jeder Mail der Marke; '' = nicht gesetzt (Mail ohne Link, Warnung im Log).
    impressumUrl: string
    // Stammdaten für die Webseite (kein Vertragswert; im Änderungsprotokoll).
    issuerPhone: string
    brandDisplayName: string
    contactEmail: string
    hotline: string
    mailFromAddress?: string
    mailFromName?: string
    // The brand's own mail texts; '' = the common one applies (server: services/mailTexts.ts).
    invoiceMailSubject?: string
    invoiceMailBody?: string
    singleInvoiceMailSubject?: string
    singleInvoiceMailBody?: string
    creditNoteMailSubject?: string
    creditNoteMailBody?: string
    smtpHost?: string
    smtpPort?: number | null
    smtpSecure?: boolean
    smtpUser?: string
    smtpPassSet?: boolean
    smtpPassHint?: string
    // 'inherit' = global account, 'own' = this brand's, 'partial' = refused
    smtpState?: 'inherit' | 'own' | 'partial'
    // What the SAVED config still lacks, by field (server: services/brandRequirements.ts). Each
    // entry is a group of fields, any one of which satisfies it (USt-IdNr. OR Steuernummer).
    requirements?: { zugferd: string[][], conditions: string[][], optional: string[] }
}

// A fee is either a percentage or a fixed amount — never both, on any level.
export type FeeType = 'percent' | 'fixed'

export interface RefundFeeOverride {
    itemId: number
    type: FeeType
    value: number
    label: string
}

export const getBrandConfigs = () =>
    api.get<BrandConfig[]>('/settings/brands').then((r) => r.data)
export const saveBrandConfig = (brand: string, patch: Record<string, unknown>) =>
    api.put<BrandConfig>(`/settings/brands/${brand}`, patch).then((r) => r.data)
// Remove a STORED secret. Its own verb on its own route on purpose: saving keeps an empty
// secret ("unverändert lassen"), so deleting cannot be expressed as a save — see
// components/SecretInput.
export const clearSettingsSecret = (field: string) =>
    api.delete<Settings>(`/settings/secrets/${field}`).then((r) => r.data)
export const clearBrandSecret = (brand: string, field: string) =>
    api.delete<BrandConfig>(`/settings/brands/${brand}/secrets/${field}`).then((r) => r.data)

export const setupBrandStripeWebhook = (brand: string) =>
    api.post<StripeWebhookSetupResult>(`/settings/brands/${brand}/stripe-webhook`).then((r) => r.data)

// ── Webhook Manager (outbound) ────────────────────────────────────────────────

export interface Webhook {
    _id: string
    name: string
    url: string
    eventType: string
    eventTypes: string[]
    enabled: boolean
    kind: string
    brand?: string
    secretSet: boolean
    secretHint: string
    lastStatus: number | null
    lastAttemptAt: string | null
    lastError: string
}
export interface WebhookEventType { value: string, label: string }
export interface WebhookTestResult { ok: boolean, status: number | null, error: string }

export const getWebhooks = () => api.get<Webhook[]>('/webhooks').then((r) => r.data)
export const getWebhookEvents = () => api.get<WebhookEventType[]>('/webhooks/events').then((r) => r.data)
export const createWebhook = (body: Record<string, unknown>) =>
    api.post<Webhook>('/webhooks', body).then((r) => r.data)
export const updateWebhook = (id: string, body: Record<string, unknown>) =>
    api.put<Webhook>(`/webhooks/${id}`, body).then((r) => r.data)
export const deleteWebhook = (id: string) =>
    api.delete(`/webhooks/${id}`).then((r) => r.data)
export const testWebhook = (id: string) =>
    api.post<WebhookTestResult>(`/webhooks/${id}/test`).then((r) => r.data)
// Reveal the stored secret in plaintext (webhooks:write) — to compare with the receiver.
export const revealWebhookSecret = (id: string) =>
    api.get<{ secret: string }>(`/webhooks/${id}/secret`).then((r) => r.data.secret)

export interface InboundEndpoint {
    brand: string
    name: string
    provider: 'stripe' | 'mollie'
    testmode: boolean
    path: string
    url: string
    // Stripe only — Mollie needs no registered endpoint / signing secret.
    testSecretSet?: boolean
    liveSecretSet?: boolean
    testEndpointSet?: boolean
    liveEndpointSet?: boolean
}
/** Zustellungen an vitalpilze, die NICHT glatt durchgingen (die Warteschlange). */
export interface VpDeliveryRow {
    id: string, deliveryId: string, event: string, label: string, attempts: number
    lastStatus?: number | null, lastError?: string, firstFailedAt?: string, gaveUpAt?: string, rejectedAt?: string | null
    nextAttemptAt?: string
}
export const getVpDeliveries = () => api.get<{
    counts: Record<string, number>, offen: VpDeliveryRow[], aufgegeben: VpDeliveryRow[], abgelehnt: VpDeliveryRow[]
}>('/webhooks/vitalpilze/deliveries').then((r) => r.data)

export const getInboundWebhooks = () => api.get<InboundEndpoint[]>('/webhooks/inbound').then((r) => r.data)
export const setupInboundWebhook = (brand: string) =>
    api.post<StripeWebhookSetupResult>(`/webhooks/inbound/${brand}/setup`).then((r) => r.data)

// ── Logs (Protokolle) ─────────────────────────────────────────────────────────

export interface LogEntry {
    _id: string
    timestamp: string
    level: string
    module: string
    message: string
    stack?: string
}
export const getLogs = (params: ListParams = {}) =>
    api.get<ListResult<LogEntry>>('/logs', { params }).then((r) => r.data)
export const clearLogs = () => api.delete<{ ok: boolean, removed: number }>('/logs').then((r) => r.data)

// Beleg-Postfach: poll the inbox now ("E-Mails abrufen").
export const pollIngest = () =>
    api.post<{ imported: number, problems: number, skipped: number, busy?: boolean }>('/ingest/poll').then((r) => r.data)

export type IngestStatus = 'imported' | 'problem' | 'ignored'
export interface IngestionItem {
    _id: string
    status: IngestStatus
    reason?: string
    from?: string
    subject?: string
    receivedAt?: string
    issueDate?: string
    amount?: number
    seller?: string
    savedFiles?: string[]
    skippedFiles?: string[]
    duplicateFiles?: string[]
    period?: { year: number, month: number }
    createdAt: string
}
export const getIngestItems = (status?: IngestStatus) =>
    api.get<IngestionItem[]>('/ingest/items', { params: status ? { status } : {} }).then((r) => r.data)
export const ingestItemAction = (id: string, action: 'retry' | 'accept' | 'ignore') =>
    api.post<{ ok: boolean, gone?: boolean }>(`/ingest/items/${id}/${action}`).then((r) => r.data)

// ---- Analytics (dashboard charts) ----
export type Granularity = 'day' | 'month' | 'year'
export interface MoneyAgg { net: number, gross: number, count: number }
export interface RevenueBucket extends MoneyAgg { bucket: string, byBrand: Record<string, MoneyAgg> }
export interface RevenueSeries {
    granularity: Granularity
    from: string
    to: string
    currency: string
    buckets: RevenueBucket[]
    totals: MoneyAgg
}
export const getRevenue = (params: { granularity: Granularity, from: string, to: string }) =>
    api.get<RevenueSeries>('/analytics/revenue', { params }).then((r) => r.data)

export interface BalanceBucket { bucket: string, income: number, expense: number, net: number }
export interface BalanceSeries {
    granularity: 'month' | 'year'
    from: string
    to: string
    currency: string
    buckets: BalanceBucket[]
    totals: { income: number, expense: number, net: number, expenseByType: Record<string, number> }
}
export const getBalance = (params: { granularity: 'month' | 'year', from: string, to: string }) =>
    api.get<BalanceSeries>('/analytics/balance', { params }).then((r) => r.data)

// ---- Expense types + vendor-domain mappings (Beleg-Zuordnung) ----
export interface ExpenseType { _id: string, key: string, label: string }
export const getExpenseTypes = () =>
    api.get<ExpenseType[]>('/expense-config/types').then((r) => r.data)
export const createExpenseType = (label: string) =>
    api.post<ExpenseType>('/expense-config/types', { label }).then((r) => r.data)
export const updateExpenseType = (id: string, label: string) =>
    api.put<ExpenseType>(`/expense-config/types/${id}`, { label }).then((r) => r.data)
export const deleteExpenseType = (id: string) =>
    api.delete(`/expense-config/types/${id}`).then((r) => r.data)

export type VendorSourceType = 'pdf' | 'link'
export interface VendorMapping { _id: string, domain: string, vendorName: string, expenseType: string, sourceType: VendorSourceType, createdAt?: string }
export const listVendorMappings = (params: ListParams = {}) =>
    api.get<ListResult<VendorMapping>>('/expense-config/mappings', { params }).then((r) => r.data)
export const createVendorMapping = (data: { domain: string, vendorName?: string, expenseType: string, sourceType?: VendorSourceType }) =>
    api.post<VendorMapping>('/expense-config/mappings', data).then((r) => r.data)
export const updateVendorMapping = (id: string, data: Partial<{ domain: string, vendorName: string, expenseType: string, sourceType: VendorSourceType }>) =>
    api.put<VendorMapping>(`/expense-config/mappings/${id}`, data).then((r) => r.data)
export const deleteVendorMapping = (id: string) =>
    api.delete(`/expense-config/mappings/${id}`).then((r) => r.data)
export const backfillVendorMappings = () =>
    api.post<{ updated: number }>('/expense-config/backfill').then((r) => r.data)

// ---- Auth / current user ----

export type Capability = 'read' | 'write' | 'delete'

export interface Me {
    _id: string
    name: string
    email: string
    role: string // Role.key
    // The role's human label ("Administrator"). Delivered with /auth/me on purpose —
    // the roles endpoint needs the `roles` module, which most users do not have.
    // Falls back to the key server-side, so it is never empty.
    roleLabel: string
    // Effective RBAC permissions per module (drives sidebar + write/delete gating).
    permissions: Record<string, Capability[]>
    preferences: UserPreferences
    currency: string // the backoffice currency (Einstellungen → Allgemein), for every role
}

// Per-user UI preferences.
//   tableColumns[tableId][columnKey] = width in px
//   tableHidden[tableId]             = column keys hidden by the user
//   tablePageSize[tableId]           = rows per page
export interface UserPreferences {
    tableColumns?: Record<string, Record<string, number>>
    tableHidden?: Record<string, string[]>
    tablePageSize?: Record<string, number>
    // Configurable dashboard: the visible widgets, in display order, each with its
    // column width. Absent → the per-user default (all widgets the role may read).
    dashboard?: { widgets: DashboardWidgetPref[] }
    [key: string]: unknown
}

export type DashboardWidgetWidth = 'narrow' | 'wide'
export interface DashboardWidgetPref {
    key: string            // unique per placed instance (allows several of one type)
    type: string           // widget type id from the registry
    width: DashboardWidgetWidth
    config?: Record<string, unknown>
    id?: string            // legacy: old prefs stored only `id` (= type = key)
}

export const getMe = () => api.get<Me>('/auth/me').then((r) => r.data)
export const login = (email: string, password: string) =>
    api.post<Me>('/auth/login', { email, password }).then((r) => r.data)
export const logout = () => api.post('/auth/logout').then((r) => r.data)
export const saveMyPreferences = (patch: Partial<UserPreferences>) =>
    api.put<UserPreferences>('/auth/me/preferences', patch).then((r) => r.data)
// Self-service account maintenance (no RBAC gate — every user manages their own).
export const updateMyProfile = (name: string) =>
    api.put<Me>('/auth/me/profile', { name }).then((r) => r.data)
export const changeMyPassword = (currentPassword: string, newPassword: string) =>
    api.post<{ ok: boolean }>('/auth/me/password', { currentPassword, newPassword }).then((r) => r.data)

// ---- Roles (admin only) ----

export interface Role {
    _id: string
    key: string
    label: string
    capabilities: Record<string, Capability[]>
    isSystem: boolean
    userCount?: number
}
export interface RolesMeta { modules: string[], capabilities: Capability[], parents?: Record<string, string>, moduleCapabilities?: Record<string, Capability[]> }

// Paginated list (Listenvorlage). For pickers/label lookups use listRoleOptions.
export const listRoles = (params: ListParams = {}) =>
    api.get<ListResult<Role>>('/roles', { params }).then((r) => r.data)
export const listRoleOptions = () =>
    listRoles({ pageSize: LOOKUP_FETCH_LIMIT, sort: 'label', dir: 'asc' }).then((r) => r.rows)
export const getRole = (id: string) => api.get<Role>(`/roles/${id}`).then((r) => r.data)
export const getRolesMeta = () => api.get<RolesMeta>('/roles/meta').then((r) => r.data)
export const createRole = (data: { key: string, label: string, capabilities: Record<string, Capability[]> }) =>
    api.post<Role>('/roles', data).then((r) => r.data)
export const updateRole = (id: string, data: { label?: string, capabilities?: Record<string, Capability[]> }) =>
    api.put<Role>(`/roles/${id}`, data).then((r) => r.data)
export const deleteRole = (id: string) => api.delete(`/roles/${id}`).then((r) => r.data)

// ---- Users (admin only) ----

export interface UserRow {
    _id: string
    name: string
    email: string
    role: string
    active: boolean
    createdAt: string
}
export interface CreateUserInput { name: string, email: string, password: string, role: string }
export interface UpdateUserInput { name?: string, role?: string, active?: boolean, password?: string }

export const listUsers = (params: ListParams = {}) =>
    api.get<ListResult<UserRow>>('/users', { params }).then((r) => r.data)
export const getUser = (id: string) => api.get<UserRow>(`/users/${id}`).then((r) => r.data)
export const createUser = (data: CreateUserInput) => api.post<UserRow>('/users', data).then((r) => r.data)
export const updateUser = (id: string, data: UpdateUserInput) =>
    api.put<UserRow>(`/users/${id}`, data).then((r) => r.data)
// Soft-delete (move to trash) + restore/purge (all admin-only).
export const deleteUser = (id: string) => api.delete(`/users/${id}`).then((r) => r.data)
export const restoreUser = (id: string) => api.post(`/users/${id}/restore`).then((r) => r.data)
export const purgeUser = (id: string) => api.delete(`/users/${id}/purge`).then((r) => r.data)

// ---- Invoice templates (XML editor + PDF preview) ----

export interface InvoiceTemplateInfo { brand: string, version: string, label: string, kind: 'invoice' | 'certificate', active: boolean }

export const listInvoiceTemplates = () =>
    api.get<InvoiceTemplateInfo[]>('/invoice-templates').then((r) => r.data)

export const getTemplateXml = (brand: string, version: string) =>
    api.get<string>(`/invoice-templates/${brand}/${version}/config.xml`, { responseType: 'text' }).then((r) => r.data)

export const saveTemplateXml = (brand: string, version: string, xml: string) =>
    api.put(`/invoice-templates/${brand}/${version}/config.xml`, { xml }).then((r) => r.data)

// ---- Template images (stored in the version folder, referenced via <image src="…">) ----

export const listTemplateImages = (brand: string, version: string) =>
    api.get<string[]>(`/invoice-templates/${brand}/${version}/images`).then((r) => r.data)

export const uploadTemplateImage = (brand: string, version: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post<{ file: string }>(`/invoice-templates/${brand}/${version}/images`, fd).then((r) => r.data)
}

export const deleteTemplateImage = (brand: string, version: string, file: string) =>
    api.delete(`/invoice-templates/${brand}/${version}/images/${encodeURIComponent(file)}`).then((r) => r.data)

export const templateImageUrl = (brand: string, version: string, file: string) =>
    `/api/invoice-templates/${brand}/${version}/images/${encodeURIComponent(file)}`

// Render the edited XML to a PDF blob (+ any interpreter warnings from the response
// header). On a 4xx the server sends a JSON error as a blob — unwrap it into a thrown
// Error so the editor can show the message.
export async function previewTemplatePdf(
    xml: string, brand: string, version: string,
): Promise<{ pdf: Blob, warnings: string[] }> {
    try {
        const r = await api.post('/invoice-templates/preview', { xml, brand, version }, { responseType: 'blob' })
        const raw = r.headers['x-template-warnings']
        const warnings = raw ? decodeURIComponent(raw).split(' | ').filter(Boolean) : []
        return { pdf: r.data as Blob, warnings }
    } catch (e: any) {
        const blob = e?.response?.data
        if (blob instanceof Blob) {
            const txt = await blob.text().catch(() => '')
            let msg = txt
            try { msg = JSON.parse(txt).error ?? txt } catch { /* keep raw text */ }
            throw new Error(msg || TX.previewFailed)
        }
        throw new Error(e?.message ?? TX.previewFailed)
    }
}

// ── Autoren-Abrechnung: Katalog + Autoren ────────────────────────────────────

export interface CatalogItem {
    _id: string
    brand: string
    itemId: number
    type: string
    title: string
    version: number
    authorId: string | null
    authorName: string | null
    authorRef: string | null
    priceSuggestion?: number
    salesPrice?: number
    salesReleased: boolean
    contentReleased: boolean
    feeType?: FeeType
    feeValue?: number
    freeAccessCount: number
    // Resolved across item → author → brand, so the list can show what actually applies.
    effectiveFee: { type: FeeType, value: number, level: 'item' | 'author' | 'global' | 'none' }
    sellable: boolean
}

export interface AuthorRow {
    _id: string
    externalRef: string
    displayName: string
    company?: string
    email: string
    address?: { line1?: string, line2?: string, postalCode?: string, city?: string, country?: string }
    vatId?: string
    // What VIES said. undefined = never confirmed (outage), false = rejected.
    vatIdValid?: boolean
    vatIdCheckedAt?: string
    /** Vom Server hergeleitet (`authorTaxCase`) — im Client nie aus dem Land ableiten. */
    taxCase?: string
    // Whether an IBAN is stored — the number itself stays on the server (payment data,
    // encrypted at rest). The mask only needs the fact, and `missing` is built from it.
    ibanSet: boolean
    bic?: string
    accountHolder?: string
    feeType?: FeeType
    feeValue?: number
    payoutThreshold: number | null   // null = folgt der Marke
    // 'paused' is a LEGACY value (the field was renamed to 'blocked' on 08.09.2026). It carries
    // no blockedAt and therefore switches nothing off — the mask says so instead of hiding it.
    status: 'active' | 'blocked' | 'paused'
    // When the block took effect. null on an author who is not blocked — and on a legacy
    // 'paused' record, which is exactly why that case is shown separately.
    blockedAt?: string | null
    notes?: string
    itemCount: number
    // What is still missing before a payout is possible.
    missing: string[]
}

// One author, for the edit tab. Same as a row plus the IBAN hint; no `missing` (the mask
// shows the empty fields themselves).
export interface AuthorDetail extends Omit<AuthorRow, 'missing'> {
    address: { line1: string, line2: string, postalCode: string, city: string, country: string }
    // '' = none (existing authors have neither).
    salutation: AuthorSalutation | ''
    title: string
    ibanHint: string
}

export const listCatalogItems = (params: { q?: string, brand?: string, release?: string, page?: number, pageSize?: number } = {}) =>
    api.get<{ rows: CatalogItem[], total: number }>('/authors/items', { params }).then((r) => r.data)
export const updateCatalogItem = (id: string, patch: Partial<Pick<CatalogItem, 'salesPrice' | 'salesReleased' | 'authorId' | 'feeType' | 'feeValue'>>) =>
    api.put(`/authors/items/${id}`, patch).then((r) => r.data)
export const publishCatalogItem = (id: string) =>
    api.post(`/authors/items/${id}/publish`).then((r) => r.data)

export const listAuthors = (params: ListParams = {}) =>
    api.get<ListResult<AuthorRow>>('/authors', { params }).then((r) => r.data)
export const getAuthor = (id: string) => api.get<AuthorDetail>(`/authors/${id}`).then((r) => r.data)
export interface VatCheckOutcome {
    vatId: string
    valid?: boolean
    checkedAt?: string
    reachable: boolean
    /** false = die geprüfte Nummer steht noch nicht im Datensatz — der Befund ist eine Auskunft
     *  und geht verloren, wenn nicht übernommen wird. */
    saved?: boolean
}

// What the edit mask may send. Not Partial<AuthorRow>: the IBAN never comes back from the
// server (see AuthorRow), but it does go TO it — empty means "keep the stored one".
export interface AuthorPatch {
    displayName?: string
    company?: string
    salutation?: AuthorSalutation | ''
    title?: string
    email?: string
    address?: { line1?: string, line2?: string, postalCode?: string, city?: string, country?: string }
    vatId?: string
    iban?: string
    bic?: string
    accountHolder?: string
    feeType?: FeeType
    feeValue?: number
    payoutThreshold?: number | null   // null = folgt der Marke
    // blockedAt is NOT in here: the server sets it when the status changes. A timestamp that
    // decides money does not come from the browser.
    status?: 'active' | 'blocked'
    // Required when a BLOCKED author is set back to active and a block period exists — the
    // server refuses the release without it. „Von Hand entscheiden, je Fall" (Betreiber,
    // 09.09.2026): the default is false, and saying so is part of the release.
    settleBlockedPeriod?: boolean
    notes?: string
}

export const updateAuthor = (id: string, patch: AuthorPatch) =>
    api.put<{ ok: boolean, vat?: VatCheckOutcome }>(`/authors/${id}`, patch).then((r) => r.data)
// Remove the stored IBAN — same contract as the settings secrets: an empty field on save
// keeps it, only this takes it away.
// What was withheld during the CURRENT block — the numbers the release dialog puts in front
// of the operator, so „per case" means something.
export interface BlockedPeriod { blockedAt: string | null, count: number, payout: number }
export const getBlockedPeriod = (id: string) =>
    api.get<BlockedPeriod>(`/authors/${id}/blocked-period`).then((r) => r.data)

// Die Verkäufe eines Autors, mit dem, was aus jedem geworden ist. ⚠️ Zwei Felder, nicht eins:
// `kind` sagt, WAS die Zeile ist, `state`, was mit ihr passiert ist — die beiden schließen sich
// nicht aus (eine Stornozeile kann längst abgerechnet sein). Die Wörter, die der Betreiber
// liest, stehen in der Maske, hier stehen Schlüssel.
export interface AuthorSaleRow {
    _id: string
    soldAt: string
    itemId: number
    title: string
    orderExternalId: string | null
    listPrice: number
    fee: number
    payout: number
    kind: 'sale' | 'reversal' | 'chargeback'
    state: 'settled' | 'excluded' | 'pending' | 'open'
    catchUpPeriod: string | null
    blockExcludedAt: string | null
}
export const listAuthorSales = (params: { authorId: string, brand?: string, period?: string }) =>
    api.get<{ rows: AuthorSaleRow[], total: number }>('/authors/sales', { params }).then((r) => r.data)

export interface RechargeResult { updated: number, skipped: number, payout: number, period: string }
export const rechargeAuthorSales = (ids: string[]) =>
    api.post<RechargeResult>('/authors/sales/recharge', { ids }).then((r) => r.data)

/** Die IBAN im Klartext — eigene Route, damit sie nicht in jeder Autoren-Antwort mitreist. */
export const getAuthorIban = (id: string) =>
    api.get<{ iban: string }>(`/authors/${id}/iban`).then((r) => r.data.iban)
export const clearAuthorIban = (id: string) => api.delete(`/authors/${id}/iban`).then((r) => r.data)
// FORMAT check only (length, country, mod-97) — it says nothing about whether the account
// exists or whose it is. Takes no author id: nothing is loaded and nothing is saved, the
// server just runs the same `ibanError` that saving and the SEPA export use, so the browser
// needs no second copy of it.
export const checkIbanFormat = (iban: string) =>
    api.post<{ ok: boolean, error: string | null, iban: string | null }>('/authors/check-iban', { iban })
        .then((r) => r.data)

// Soft-delete (move to trash). „Deleted" means inactive: he keeps his sales and his past
// settlements, and earns no new credit notes from that moment on — the cut runs along the
// timestamp, see the server's buildStatement. Restore/purge are admin-only on the server.
export const deleteAuthor = (id: string) => api.delete(`/authors/${id}`).then((r) => r.data)
export const restoreAuthor = (id: string) => api.post(`/authors/${id}/restore`).then((r) => r.data)
export const purgeAuthor = (id: string) => api.delete(`/authors/${id}/purge`).then((r) => r.data)

/** ⚠️ Die EINGETIPPTE Nummer wird mitgeschickt. Ohne sie prueft der Server die GESPEICHERTE —
 *  und der Knopf gaebe ein Urteil ueber etwas ab, das im Formular nicht mehr steht. */
export const checkAuthorVat = (id: string, vatId?: string) =>
    api.post<VatCheckOutcome>(`/authors/${id}/check-vat`, { vatId }).then((r) => r.data)

export interface StatementTotals {
    base: number, fee: number, payout: number, vat: number, gross: number
    carriedIn: number, toPay: number, carriedOut: number
}

export interface StatementRow {
    _id: string
    brand: string
    period: string
    // ⚠️ 'pending' ist die Zeile, die es NOCH NICHT GIBT: ein Autor mit Umsatz im Monat, fuer
    // den noch keine Abrechnung erzeugt wurde. Sie kommt aus derselben Rechnung wie der Lauf
    // (computeStatement), traegt aber kein `_id` — man kann sie weder ansehen noch
    // festschreiben, nur entstehen lassen.
    status: 'draft' | 'finalized' | 'paid' | 'pending'
    /** WO die Gutschrift auf dem Weg zum Geld steht — abgeleitet, nicht gespeichert.
     *
     *  ⚠️ Etwas anderes als `status`: Der sagt, wie weit das DOKUMENT ist (Entwurf, ausgestellt,
     *  bezahlt), dieser hier, wie weit die ZAHLUNG ist. Der Unterschied ist `submitted`: Die
     *  SEPA-Datei ist erzeugt, aber noch nicht bei der Bank hochgeladen oder von ihr angenommen —
     *  eine erzeugte Datei ist kein geflossenes Geld.
     *
     *  Fehlt an Entwuerfen und Anwaertern: Was keine Belegnummer hat, ist auf keinem Weg. */
    paymentState?: 'open' | 'submitted' | 'paid'
    /** Wann diese Gutschrift in eine Ueberweisungsdatei kam — und in welche. */
    sepaExportedAt?: string | null
    sepaExportId?: string
    documentNumber?: string
    issueDate?: string
    paidAt?: string
    authorId: string
    authorName: string
    lineCount: number
    totals: StatementTotals
    freeAccessCount: number
    hasPdf: boolean
    /** Dateiname der PDF: Tab-Titel und Ende ihrer Adresse (58a). */
    pdfName: string
    // Empty unless the draft cannot be finalized yet.
    missing: string[]
    /** Der VIES-Stand, der zu DIESER Zeile gehoert — aus dem Snapshot der Abrechnung, nicht
     *  vom Autor von heute. Fehlt, wenn keine USt-IdNr. hinterlegt ist. */
    vat?: { vatId?: string, vatIdValid?: boolean, vatIdCheckedAt?: string, taxCase?: string }
    /** Gesetzt an einer `pending`-Zeile, die zu einem BESTEHENDEN Entwurf desselben Autors
     *  gehoert: die Id dieses Entwurfs. Der Autor steht dann zweimal in der Liste — einmal mit
     *  dem, was im Dokument ist, einmal mit dem, was beim naechsten Lauf dazukommt. Der Zustand
     *  haengt an der Zeile, nicht am Autor. */
    partOfDraft?: string
}

export const listStatements = (params: { period?: string, brand?: string, status?: string } = {}) =>
    api.get<{ rows: StatementRow[], total: number }>('/authors/statements', { params }).then((r) => r.data)
export const statementPeriods = () =>
    api.get<{ periods: string[], suggestion: string }>('/authors/statement-periods').then((r) => r.data)
export interface MonthlyRunResult {
    period: string
    built: number
    finalized: number
    /** Was als Entwurf liegen blieb — MIT dem Grund. Ohne diese Liste waere „12 abgerechnet"
     *  eine Meldung, hinter der drei unvollstaendige Autoren verschwinden. */
    pending: { authorId: string, authorName: string, missing: string[] }[]
    unchanged: number
    failed: { authorId: string, error: string }[]
}
export const runStatements = (brand: string, year: number, month: number) =>
    api.post<MonthlyRunResult>('/authors/statements/run', { brand, year, month }).then((r) => r.data)
export const rebuildStatement = (id: string) =>
    api.post(`/authors/statements/${id}/rebuild`).then((r) => r.data)
export const finalizeStatement = (id: string) =>
    api.post<{ documentNumber: string }>(`/authors/statements/${id}/finalize`).then((r) => r.data)
export const markStatementPaid = (id: string, paymentRef?: string) =>
    api.post(`/authors/statements/${id}/paid`, { paymentRef }).then((r) => r.data)

// ⛔ ZWEI Rueckwege, und sie duerfen nicht zusammenfallen. Der Unterschied entscheidet, ob die
// Gutschrift in die naechste Ueberweisungsdatei kommt:
//   unmarkStatementPaid   — nur falsch markiert. Der Export bleibt stehen, sie kommt NICHT
//                           erneut in eine Datei. Sonst bekaeme der Autor sein Geld zweimal.
//   statementPaymentReturned — die Bank hat die Ueberweisung zurueckgegeben. Der Exportvermerk
//                           faellt weg, damit sie in die naechste Datei kommt.
export const unmarkStatementPaid = (id: string) =>
    api.post(`/authors/statements/${id}/unpay`).then((r) => r.data)
export const statementPaymentReturned = (id: string) =>
    api.post(`/authors/statements/${id}/payment-returned`).then((r) => r.data)

/** Ein Auszahlungslauf = eine erzeugte SEPA-Datei, erkennbar an ihrer MsgId. */
export interface SepaRun {
    sepaExportId: string
    exportedAt: string
    count: number
    total: number
    paidCount: number
}
export const sepaRuns = (brand: string, period: string) =>
    api.get<{ runs: SepaRun[] }>('/authors/sepa/runs', { params: { brand, period } }).then((r) => r.data.runs)
/** ⛔ EIN Klick fuer den ganzen Lauf — eine Datei, ein Upload, eine Bestaetigung. */
export const markSepaRunPaid = (msgId: string) =>
    api.post<{ marked: number }>(`/authors/sepa/runs/${encodeURIComponent(msgId)}/paid`).then((r) => r.data)
// ── SEPA-Sammelauftrag ────────────────────────────────────────────────────────
export interface SepaLine {
    statementId: string
    documentNumber: string
    authorName: string
    /** Maskiert — die Vorschau bekommt die IBAN nie im Klartext. */
    iban: string
    bic?: string
    amount: number
    remittance: string
}
export interface SepaSkipped {
    documentNumber: string, authorName: string, reason: string
    /** Zurueckgehalten, weil die IBAN nicht die beim Festschreiben ist — bestaetigbar. */
    ibanCheck?: { statementId: string, iban: string, reason: 'changed' | 'unknown' }
}

/** Der Betreiber bestaetigt die (geaenderte) IBAN fuer EINE Gutschrift. Die gesehene IBAN geht
 *  mit; hat sie sich inzwischen erneut geaendert, lehnt der Server ab (409). */
export const confirmStatementIban = (statementId: string, iban: string) =>
    api.post(`/authors/statements/${statementId}/confirm-iban`, { iban }).then((r) => r.data)

export const sepaPreview = (brand: string, period: string, includeExported = false) =>
    api.get<{ lines: SepaLine[], total: number, skipped: SepaSkipped[] }>(
        '/authors/sepa/preview', { params: { brand, period, includeExported } }).then((r) => r.data)

/** ⚠️ Setzt den Export-Vermerk und liefert die Datei. Sie wird NICHT auf dem Server abgelegt —
 *  sie enthaelt die Bankverbindungen im Klartext. */
export const sepaExport = (brand: string, period: string, includeExported = false) =>
    api.post<{ msgId: string, xml: string, count: number, total: number, skipped: SepaSkipped[] }>(
        '/authors/sepa/export', { brand, period, includeExported }).then((r) => r.data)

export const statementPdfUrl = (id: string, name: string) => `${api.defaults.baseURL}/authors/statements/${id}/pdf/${encodeURIComponent(name)}`
// Die Verkaufszeilen einer Listenzeile, nachgeladen beim Aufklappen. Deckt beide Faelle ab:
// ein Entwurf liefert seine gespeicherten Zeilen, ein Anwaerter die gerechneten.
export const statementLines = (brand: string, period: string, authorId: string) =>
    api.get<{ lines: StatementLine[] }>('/authors/statements/lines', { params: { brand, period, authorId } })
        .then((r) => r.data)

export interface StatementLine {
    saleId: string
    itemId: number
    title: string
    soldAt: string
    orderExternalId?: string
    invoiceNumber?: string
    listPrice: number
    fee: number
    payout: number
    isReversal: boolean
    reversalReason?: 'refund' | 'chargeback' | null
    isChargebackFee?: boolean
    isCatchUp: boolean
    /** false = diese Zeile steht NOCH NICHT im Entwurf und kommt beim naechsten Lauf dazu.
     *  Bei einer festgeschriebenen Abrechnung ist es immer true — dort kommt nichts mehr dazu. */
    inDraft: boolean
}

// ---- Zertifikat-Versandliste (Selbstversand) ----
// Kein Druckdienstleister kann Urkundenkarton UND Direktversand, also druckt und kuvertiert
// ein Mensch. Diese Liste ist seine Arbeitsanweisung.

export interface DispatchShipping {
    firstName?: string
    lastName?: string
    company?: string
    line1?: string
    line2?: string
    postalCode?: string
    city?: string
    country?: string
}

export interface DispatchRow {
    id: string
    certificateNumber: string
    // Dateiname des Zertifikats: Tab-Titel und Ende seiner Adresse (58a).
    pdfName: string
    holderName: string
    holderEmail: string
    courseTitle: string
    orderExternalId?: string
    orderedAt?: string
    shipping?: DispatchShipping
    // Falsch heisst: bezahlt, aber die Anschrift taugt nicht zum Verschicken. Die Zeile bleibt
    // trotzdem stehen — genau die muss jemand anfassen.
    addressComplete: boolean
    sentAt?: string
    sentBy?: string
    // Wie oft die Anschrift von Hand korrigiert wurde, und von wem zuletzt. Steht in der Liste,
    // damit eine korrigierte Zeile als solche erkennbar ist.
    addressChanges: number
    addressChangedAt?: string
    addressChangedBy?: string
}

// Etikettenkatalog fuer die Auswahlliste in den Einstellungen. Kommt vom Server, damit Maße
// und Raster genau EINE Quelle haben — eine zweite Liste im Client waere die naechste Stelle,
// an der ein Millimeter abweicht.
export interface LabelPreset {
    key: string
    widthMm: number
    heightMm: number
    columns: number
    rows: number
    marginTopMm: number
    marginLeftMm: number
    gapXMm: number
    gapYMm: number
    fillDirection: 'row' | 'column'
}

export const listLabelPresets = () =>
    api.get<LabelPreset[]>('/settings/label-presets').then((r) => r.data)

export const listDispatch = () =>
    api.get<{ pending: DispatchRow[], recent: DispatchRow[] }>('/certificates/dispatch').then((r) => r.data)

export const markDispatchSent = (id: string) =>
    api.post<DispatchRow>(`/certificates/dispatch/${id}/sent`).then((r) => r.data)

export const undoDispatchSent = (id: string) =>
    api.post<DispatchRow>(`/certificates/dispatch/${id}/undo`).then((r) => r.data)

// Anschrift von Hand korrigieren. Aendern, nicht nur nachtragen — eine falsche Hausnummer
// merkt man erst, wenn der Brief zurueckkommt, und dann ist das Feld laengst nicht mehr leer.
// Nach dem Versand antwortet der Server mit 409: dann erst den Haken zuruecknehmen.
export const updateDispatchAddress = (id: string, shipping: DispatchShipping) =>
    api.put<DispatchRow>(`/certificates/dispatch/${id}/address`, shipping).then((r) => r.data)

// Adressdruck. `start` ist die Etikettennummer auf dem Bogen, bei der begonnen wird — ein
// angebrochener Bogen ist der Normalfall, und wer dafuer in die Einstellungen muss, macht es
// nicht. Beim Umschlagdruck wird der Wert ignoriert.
export const dispatchAddressesUrl = (ids: string[], start?: number) =>
    `/api/certificates/dispatch/addresses.pdf?ids=${encodeURIComponent(ids.join(','))}`
    + (start && start > 1 ? `&start=${start}` : '')

export const dispatchCertificateUrl = (id: string, name: string) =>
    `/api/certificates/dispatch/${id}/pdf/${encodeURIComponent(name)}`

// ── Arbeitsliste (Dashboard) ─────────────────────────────────────────────────────
// ⛔ Es gibt keinen Loeschweg, und das ist kein Versehen: Ein erledigter Punkt wird
// ausgeblendet, nicht entfernt. Das Entfernen bleibt ein Schritt des Betreibers.
export interface Todo {
    _id: string
    text: string
    doneAt: string | null
    source: string
    createdAt: string
}

export const listTodos = (done = false) =>
    api.get<{ rows: Todo[] }>('/todos', { params: done ? { done: 1 } : {} }).then((r) => r.data.rows)

export const createTodo = (text: string) =>
    api.post<{ _id: string }>('/todos', { text }).then((r) => r.data)

/** `done: false` holt einen Punkt zurueck — derselbe Weg, damit ein Fehlklick keine
 *  Sackgasse ist. */
export const setTodoDone = (id: string, done: boolean) =>
    api.patch<{ _id: string, doneAt: string | null }>(`/todos/${id}`, { done }).then((r) => r.data)

// ── Mail-Rahmen (server: email/frames.ts, routes/mailFrames.ts) ─────────────────────────────
// Reihenfolge beim Versand: Rahmen der Marke → gemeinsamer Rahmen → Vorgabe im Programm.
export interface MailFrameValues { logoWidth?: number | '', footerColor?: string, footerTextColor?: string }
export interface MailFrameBrand {
    brand: string
    name: string
    frame: string | null                          // null = kein eigener Rahmen, der gemeinsame gilt
    activeSource: 'brand' | 'common' | 'default'
    values: MailFrameValues                       // nur was gesetzt ist
    effectiveValues: { logoWidth: number, footerColor: string, footerTextColor: string }
    defaults: { logoWidth: number, footerColor: string, footerTextColor: string }
    logoUploaded: boolean
}
export interface MailFramesState {
    placeholders: string[]
    common: { html: string, stored: boolean }
    defaultFrame: string
    brands: MailFrameBrand[]
}

const errorOf = (e: any, fallback: string) => new Error(e?.response?.data?.error ?? e?.message ?? fallback)

export const getMailFrames = () => api.get<MailFramesState>('/mail-frames').then((r) => r.data)
export const saveCommonMailFrame = (html: string) =>
    api.put('/mail-frames/common', { html }).then((r) => r.data).catch((e) => { throw errorOf(e, TX.saveFailed) })
export const resetCommonMailFrame = () => api.delete('/mail-frames/common').then((r) => r.data)
export const saveBrandMailFrame = (brand: string, html: string) =>
    api.put<MailFrameBrand>(`/mail-frames/brands/${brand}/frame`, { html }).then((r) => r.data).catch((e) => { throw errorOf(e, TX.saveFailed) })
export const resetBrandMailFrame = (brand: string) =>
    api.delete<MailFrameBrand>(`/mail-frames/brands/${brand}/frame`).then((r) => r.data)
export const saveMailFrameValues = (brand: string, values: MailFrameValues) =>
    api.put<MailFrameBrand>(`/mail-frames/brands/${brand}/values`, values).then((r) => r.data).catch((e) => { throw errorOf(e, TX.saveFailed) })
export const mailFrameLogoUrl = (brand: string, bust: number) => `/api/mail-frames/brands/${brand}/logo?v=${bust}`
export function uploadMailFrameLogo(brand: string, file: File) {
    const fd = new FormData()
    fd.append('file', file)
    return api.post<MailFrameBrand>(`/mail-frames/brands/${brand}/logo`, fd).then((r) => r.data).catch((e) => { throw errorOf(e, TX.uploadFailed) })
}
export const resetMailFrameLogo = (brand: string) =>
    api.delete<MailFrameBrand>(`/mail-frames/brands/${brand}/logo`).then((r) => r.data)
// Which text each mail of a brand gets — same rule as the sending (server: services/mailTexts.ts).
export type MailTextKind = 'invoice' | 'single' | 'creditNote'
export type MailTextSource = 'customer' | 'brandSingle' | 'brandInvoice' | 'brandCreditNote'
    | 'commonSingle' | 'commonInvoice' | 'commonCreditNote'
export interface MailTextPreview { kind: MailTextKind, subject: string, html: string, subjectSource: MailTextSource, bodySource: MailTextSource }
export const previewMailTexts = (brand: string, texts?: Record<string, string>) =>
    api.post<MailTextPreview[]>('/mail-frames/texts/preview', { brand, texts }).then((r) => r.data)
        .catch((e) => { throw errorOf(e, TX.previewFailed) })
export const previewMailFrame = (brand: string, frame?: string, values?: MailFrameValues) =>
    api.post<string>('/mail-frames/preview', { brand, frame, values }, { responseType: 'text' }).then((r) => r.data)
        .catch((e) => { throw errorOf(e, TX.previewFailed) })
